// ─── StockByVehicleView — stock por viatura (Petrotec Angola) ────────────
//
// Operador selecciona uma viatura e vê os saldos de stock. Pode:
//   · Reabastecer central (replenish_central) — entrada no armazém
//   · Transferir para viatura (load_vehicle)  — debita central, credita viatura
//   · Transferir da viatura para central (unload_vehicle) — sobra/devolução
//   · Ajuste (adjust) — correcção positiva ou negativa
//
// Todos os movimentos passam por POST /planner-api/stock-movements. O
// histórico é mostrado num drawer/secção lateral (timeline cronológica).

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { getTenantCurrency } from '../../utils/manageHelpers';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip,
  CircularProgress, Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import Inventory2Icon from '@material-ui/icons/Category';
import HistoryIcon from '@material-ui/icons/History';
import AddIcon from '@material-ui/icons/Add';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import LocalShipping2Icon from '@material-ui/icons/LocalShipping';
import { useDispatch } from 'react-redux';
import { setActiveView } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    background: '#f8fafc', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff', borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  breadcrumb: {
    fontSize: '0.68rem', color: '#64748b', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap',
  },
  crumbLink: { cursor: 'pointer', color: '#dc2626', '&:hover': { textDecoration: 'underline' } },
  title: {
    fontSize: '1.05rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  toolbar: {
    display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  body: { flex: 1, display: 'flex', overflow: 'hidden' },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  drawer: {
    width: 380, borderLeft: '1px solid #e2e8f0', background: '#fff',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  drawerHeader: {
    padding: '12px 16px', borderBottom: '1px solid #e2e8f0',
    fontSize: '0.78rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  drawerList: { flex: 1, overflowY: 'auto', padding: '8px 12px' },
  movementRow: {
    padding: '8px 10px', borderBottom: '1px solid #f1f5f9',
    fontSize: '0.7rem',
  },
  card: {
    background: '#fff', borderRadius: 12, padding: '12px 16px', marginBottom: 8,
    border: '1px solid #e2e8f0',
    display: 'grid',
    gridTemplateColumns: '1fr auto auto auto auto',
    gap: 10, alignItems: 'center',
  },
  cardZero: { opacity: 0.5 },
  cardName: {
    fontSize: '0.88rem', fontWeight: 700, color: '#0f172a',
  },
  cardMeta: { fontSize: '0.66rem', color: '#64748b', marginTop: 2 },
  qty: {
    fontSize: '0.92rem', fontWeight: 800, color: '#0f172a',
    minWidth: 90, textAlign: 'right',
  },
  qtyZero: { color: '#94a3b8' },
  actionBtn: {
    fontSize: '0.66rem', fontWeight: 700, textTransform: 'none',
    padding: '4px 10px', borderRadius: 6, minWidth: 80,
  },
  formCard: {
    background: '#fff', borderRadius: 12, padding: 18, marginBottom: 12,
    border: '2px solid #3b82f6',
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
  empty: {
    padding: '60px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
  },
}));

const MOVEMENT_TYPES = [
  { value: 'replenish_central', labelPt: 'Entrada no central', labelEn: 'Replenish central', needsVehicle: false, color: '#22c55e' },
  { value: 'load_vehicle',      labelPt: 'Carregar viatura',   labelEn: 'Load vehicle',       needsVehicle: true,  color: '#3b82f6' },
  { value: 'unload_vehicle',    labelPt: 'Devolver ao central',labelEn: 'Unload to central',  needsVehicle: true,  color: '#f59e0b' },
  { value: 'adjust',            labelPt: 'Ajustar',            labelEn: 'Adjust',             needsVehicle: false, color: '#a855f7' },
];

const MOVEMENT_LABEL = MOVEMENT_TYPES.reduce((acc, t) => {
  acc[t.value] = t; return acc;
}, {});

function StockByVehicleView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  const tenantCurrency = getTenantCurrency(clientSettings);
  const pt = lang === 'pt';

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [items, setItems] = useState([]);           // catálogo
  const [balances, setBalances] = useState([]);     // saldos por item
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [movementForm, setMovementForm] = useState(null); // {type, stockItemId, quantity, notes}
  const [saving, setSaving] = useState(false);
  // Transferência de stock entre viaturas (útil quando uma viatura sai da
  // frota). Mostra dialog com select da viatura destino.
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferToId, setTransferToId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [transferring, setTransferring] = useState(false);
  // Vista "Resumo de stock": agrega para todas as viaturas o total de
  // referências distintas + total de unidades (e valor se preço definido).
  // Carregada em paralelo no mount; refrescada quando algum movimento muda.
  const [overview, setOverview] = useState([]); // [{vehicleId, plate, isActive, items, units, value}]
  const [loadingOverview, setLoadingOverview] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Carregar viaturas + catálogo (uma vez)
  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch('/planner-api/vehicles', { headers }).then(r => r.ok ? r.json() : []),
      fetch('/planner-api/stock-items', { headers }).then(r => r.ok ? r.json() : []),
    ]).then(([vs, its]) => {
      const list = Array.isArray(vs) ? vs : (vs.data || []);
      // Incluir TODAS as viaturas (inclusive inactivas) para o operador
      // poder ver/transferir stock de viaturas desactivadas. A UI marca-as
      // visualmente para evitar confusão.
      setVehicles(list);
      setItems(Array.isArray(its) ? its : []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Quando muda a viatura, carregar saldos + movimentos
  const fetchVehicleData = useCallback(async () => {
    if (!token || !selectedVehicleId) {
      setBalances([]); setMovements([]); return;
    }
    setLoading(true);
    setError(null);
    try {
      const [bRes, mRes] = await Promise.all([
        fetch(`/planner-api/vehicle-stock?vehicleId=${selectedVehicleId}&includeZero=1`, { headers }),
        fetch(`/planner-api/stock-movements?vehicleId=${selectedVehicleId}&limit=100`, { headers }),
      ]);
      if (!bRes.ok) throw new Error('Falha a carregar saldos');
      if (!mRes.ok) throw new Error('Falha a carregar movimentos');
      const bData = await bRes.json();
      const mData = await mRes.json();
      setBalances(bData.balances || []);
      setMovements(Array.isArray(mData) ? mData : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedVehicleId]);

  useEffect(() => { fetchVehicleData(); }, [fetchVehicleData]);

  // Vista resumo: para cada viatura, pedir o saldo agregado. Como o
  // endpoint actual é per-vehicle, paralelizamos as chamadas. Em frotas
  // pequenas (~10-20 viaturas) é instantâneo. Para frotas maiores
  // considerar futuramente um endpoint dedicado /vehicle-stock/summary.
  const fetchOverview = useCallback(async () => {
    if (!token || vehicles.length === 0) return;
    setLoadingOverview(true);
    try {
      const results = await Promise.all(vehicles.map(async (v) => {
        try {
          const r = await fetch(`/planner-api/vehicle-stock?vehicleId=${v.id}`, { headers });
          if (!r.ok) return { vehicleId: v.id, plate: v.plate, isActive: v.isActive, items: 0, units: 0, value: 0, balances: [] };
          const data = await r.json();
          const bals = data.balances || [];
          const items = bals.length;
          const units = bals.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0);
          const value = bals.reduce((sum, b) => sum + ((Number(b.quantity) || 0) * (Number(b.unitPrice) || 0)), 0);
          return {
            vehicleId: v.id, plate: v.plate, type: v.type, isActive: v.isActive,
            items, units, value, balances: bals,
          };
        } catch {
          return { vehicleId: v.id, plate: v.plate, isActive: v.isActive, items: 0, units: 0, value: 0, balances: [] };
        }
      }));
      // Ordenar: com stock primeiro, depois alfabético
      results.sort((a, b) => {
        if ((a.items > 0) !== (b.items > 0)) return a.items > 0 ? -1 : 1;
        return String(a.plate).localeCompare(String(b.plate));
      });
      setOverview(results);
    } catch (e) {
      console.warn('Overview fetch error:', e);
    } finally {
      setLoadingOverview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, vehicles]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Construir vista combinada: catálogo + saldo (0 se não há linha)
  const combined = items.filter(it => it.isActive).map(it => {
    const bal = balances.find(b => b.stockItemId === it.id);
    return {
      ...it,
      vehicleQty: bal ? bal.quantity : 0,
    };
  });

  function openMovementForm(type, stockItemId = null) {
    setMovementForm({
      type,
      stockItemId: stockItemId || (items[0]?.id || ''),
      quantity: '',
      notes: '',
    });
  }

  async function saveMovement() {
    if (!movementForm) return;
    if (!movementForm.stockItemId) { setError(pt ? 'Escolhe um material.' : 'Pick a material.'); return; }
    if (!movementForm.quantity) { setError(pt ? 'Quantidade obrigatória.' : 'Quantity required.'); return; }
    const meta = MOVEMENT_LABEL[movementForm.type];
    if (meta?.needsVehicle && !selectedVehicleId) {
      setError(pt ? 'Selecciona uma viatura.' : 'Pick a vehicle.'); return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        type: movementForm.type,
        stockItemId: parseInt(movementForm.stockItemId),
        vehicleId: meta?.needsVehicle ? parseInt(selectedVehicleId) : undefined,
        quantity: Number(movementForm.quantity),
        notes: movementForm.notes || null,
      });
      const r = await fetch('/planner-api/stock-movements', {
        method: 'POST', headers, body,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setMovementForm(null);
      await fetchVehicleData();
      await fetchOverview();
      // Refetch catálogo para actualizar centralStock
      const refreshed = await fetch('/planner-api/stock-items', { headers }).then(r => r.ok ? r.json() : []);
      setItems(Array.isArray(refreshed) ? refreshed : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const selectedVehicle = vehicles.find(v => String(v.id) === String(selectedVehicleId));
  const selectedIsInactive = selectedVehicle && selectedVehicle.isActive === false;
  const activeVehicles = vehicles.filter(v => v.isActive !== false && v.id !== selectedVehicleId);

  async function doTransfer() {
    if (!selectedVehicleId || !transferToId) return;
    setTransferring(true);
    setError(null);
    try {
      const r = await fetch('/planner-api/stock-movements/transfer-vehicle', {
        method: 'POST', headers,
        body: JSON.stringify({
          fromVehicleId: parseInt(selectedVehicleId),
          toVehicleId: parseInt(transferToId),
          notes: transferNotes || null,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setTransferOpen(false);
      setTransferToId('');
      setTransferNotes('');
      await fetchVehicleData();
      await fetchOverview();
      // refresh catálogo para refrescar centrais (passaram pelo central)
      const refreshed = await fetch('/planner-api/stock-items', { headers }).then(r => r.ok ? r.json() : []);
      setItems(Array.isArray(refreshed) ? refreshed : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setTransferring(false);
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Materiais' : 'Back to Materials'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('stockItems'))}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Box className={classes.breadcrumb}>
            <span className={classes.crumbLink} onClick={() => dispatch(setActiveView('maintenance'))}>
              {pt ? 'Manutenção' : 'Maintenance'}
            </span>
            <span>›</span>
            <span className={classes.crumbLink} onClick={() => dispatch(setActiveView('stockItems'))}>
              {pt ? 'Materiais' : 'Materials'}
            </span>
            <span>›</span>
            <span style={{ fontWeight: 700, color: '#0f172a' }}>
              {pt ? 'Stock por viatura' : 'Stock by vehicle'}
            </span>
          </Box>
          <Typography className={classes.title}>
            <LocalShippingIcon style={{ fontSize: 20, color: '#3b82f6' }} />
            {pt ? 'Stock por viatura' : 'Stock by vehicle'}
            <span style={{ marginLeft: 10, padding: '2px 8px', background: '#f0f9ff', color: '#0369a1', border: '1px solid #e0f2fe', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700 }}>
              {tenantCurrency.symbol}
            </span>
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchVehicleData} disabled={loading || !selectedVehicleId}>
            <RefreshIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <Autocomplete
          size="small"
          style={{ minWidth: 360, maxWidth: 460 }}
          options={vehicles}
          value={vehicles.find(v => String(v.id) === String(selectedVehicleId)) || null}
          onChange={(_, v) => { setSelectedVehicleId(v?.id || ''); setMovementForm(null); }}
          getOptionLabel={(v) => v?.plate || ''}
          getOptionSelected={(opt, val) => String(opt.id) === String(val.id)}
          // Ordenar: com stock primeiro, depois alfabético — mesma lógica do overview
          filterOptions={(opts, state) => {
            const q = state.inputValue.trim().toLowerCase();
            const filtered = q
              ? opts.filter(v => String(v.plate || '').toLowerCase().includes(q)
                  || String(v.type || '').toLowerCase().includes(q))
              : opts;
            // Marcar viaturas com stock para ordenar
            const withStock = new Set(overview.filter(o => o.items > 0).map(o => o.vehicleId));
            return [...filtered].sort((a, b) => {
              const aHas = withStock.has(a.id) ? 1 : 0;
              const bHas = withStock.has(b.id) ? 1 : 0;
              if (aHas !== bHas) return bHas - aHas;
              return String(a.plate).localeCompare(String(b.plate));
            });
          }}
          renderOption={(v) => {
            const stockRow = overview.find(o => o.vehicleId === v.id);
            const stockItems = stockRow?.items || 0;
            const isTruck = (v.type || '').toLowerCase().includes('truck') || (v.type || '').toLowerCase().includes('cami');
            const Icon = isTruck ? LocalShipping2Icon : DirectionsCarIcon;
            const initials = String(v.plate || '?').replace(/[^A-Z0-9]/gi, '').slice(0, 2);
            return (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '4px 0' }}>
                {/* Avatar com iniciais */}
                <Box style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: v.isActive === false ? '#fee2e2' : (stockItems > 0 ? '#dcfce7' : '#f1f5f9'),
                  color: v.isActive === false ? '#991b1b' : (stockItems > 0 ? '#166534' : '#475569'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.74rem', fontWeight: 800, flexShrink: 0,
                }}>
                  {initials}
                </Box>
                {/* Texto principal */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>
                      {v.plate}
                    </Typography>
                    {v.isActive === false && (
                      <Chip label={pt ? 'Inactiva' : 'Inactive'} size="small"
                        style={{ height: 16, fontSize: '0.54rem', background: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
                    )}
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                    <Icon style={{ fontSize: 12, color: '#94a3b8' }} />
                    <Typography style={{ fontSize: '0.66rem', color: '#64748b' }}>
                      {v.type || (pt ? 'sem tipo' : 'no type')}
                    </Typography>
                  </Box>
                </Box>
                {/* Badge stock à direita */}
                {stockItems > 0 ? (
                  <Chip label={`${stockItems} ${pt ? 'refs' : 'refs'}`} size="small"
                    style={{
                      height: 20, fontSize: '0.62rem', fontWeight: 800,
                      background: '#dcfce7', color: '#166534',
                    }} />
                ) : (
                  <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                    {pt ? 'sem stock' : 'no stock'}
                  </Typography>
                )}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label={pt ? 'Viatura' : 'Vehicle'}
              placeholder={pt ? 'Procurar matrícula…' : 'Search plate…'}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedVehicle ? (
                  <Box style={{
                    width: 26, height: 26, borderRadius: 7, marginLeft: 4,
                    background: selectedIsInactive ? '#fee2e2' : '#dbeafe',
                    color: selectedIsInactive ? '#991b1b' : '#1d4ed8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.62rem', fontWeight: 800,
                  }}>
                    {String(selectedVehicle.plate || '?').replace(/[^A-Z0-9]/gi, '').slice(0, 2)}
                  </Box>
                ) : null,
              }}
            />
          )}
        />
        {selectedIsInactive && (
          <Chip label={pt ? '⚠ Viatura inactiva — transfere o stock para outra' : '⚠ Inactive vehicle — transfer stock to another'}
            size="small"
            style={{ background: '#fef2f2', color: '#991b1b', fontWeight: 700, fontSize: '0.66rem', height: 22 }} />
        )}
        {selectedVehicleId && (
          <>
            <Button size="small" startIcon={<AddIcon style={{ fontSize: 14 }} />}
              onClick={() => openMovementForm('replenish_central')}
              style={{ textTransform: 'none', fontSize: '0.7rem', color: '#16a34a' }}>
              {pt ? 'Entrada central' : 'Replenish central'}
            </Button>
            <Button size="small" startIcon={<AddIcon style={{ fontSize: 14 }} />}
              onClick={() => openMovementForm('load_vehicle')}
              style={{ textTransform: 'none', fontSize: '0.7rem', color: '#2563eb' }}>
              {pt ? 'Carregar viatura' : 'Load vehicle'}
            </Button>
            <Button size="small"
              onClick={() => openMovementForm('adjust')}
              style={{ textTransform: 'none', fontSize: '0.7rem', color: '#a855f7' }}>
              {pt ? 'Ajustar' : 'Adjust'}
            </Button>
            <Button size="small"
              onClick={() => setTransferOpen(true)}
              disabled={balances.every(b => b.quantity === 0)}
              style={{ textTransform: 'none', fontSize: '0.7rem', color: '#0ea5e9' }}>
              {pt ? '→ Transferir' : '→ Transfer'}
            </Button>
          </>
        )}
      </Box>

      {/* Dialog de transferência de stock entre viaturas */}
      {transferOpen && (
        <Box style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500,
        }} onClick={() => !transferring && setTransferOpen(false)}>
          <Box onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
            <Typography style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
              {pt ? 'Transferir stock da viatura' : 'Transfer vehicle stock'}
            </Typography>
            <Typography style={{ fontSize: '0.74rem', color: '#64748b' }}>
              {pt
                ? `Vai transferir TODO o stock de ${selectedVehicle?.plate} para outra viatura. Útil quando esta viatura sai da frota. Cria movimentos de saída + entrada para histórico claro.`
                : `Will transfer ALL stock from ${selectedVehicle?.plate} to another vehicle.`}
            </Typography>
            <FormControl size="small" variant="outlined">
              <InputLabel>{pt ? 'Viatura destino' : 'Destination vehicle'}</InputLabel>
              <Select label={pt ? 'Viatura destino' : 'Destination vehicle'}
                value={transferToId}
                onChange={e => setTransferToId(e.target.value)}>
                {activeVehicles.map(v => (
                  <MenuItem key={v.id} value={v.id}>{v.plate}{v.type ? ` · ${v.type}` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={pt ? 'Notas (opcional)' : 'Notes'}
              size="small" variant="outlined" multiline minRows={2}
              value={transferNotes}
              onChange={e => setTransferNotes(e.target.value)}
              placeholder={pt ? 'Motivo da transferência…' : 'Reason…'} />
            <Box style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setTransferOpen(false)} disabled={transferring}
                style={{ textTransform: 'none', fontSize: '0.78rem' }}>
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={doTransfer} disabled={transferring || !transferToId}
                style={{
                  background: '#0ea5e9', color: '#fff',
                  textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
                  padding: '6px 18px', boxShadow: 'none',
                }}>
                {transferring ? (pt ? 'A transferir…' : 'Transferring…') : (pt ? 'Transferir' : 'Transfer')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box className={classes.body}>
        <Box className={classes.list}>
          {error && (
            <Box style={{
              padding: '10px 14px', background: '#fef2f2',
              border: '1px solid #fecaca', color: '#991b1b',
              borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
            }}>{error}</Box>
          )}

          {movementForm && (
            <Box className={classes.formCard}>
              <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: '#3b82f6' }}>
                {MOVEMENT_LABEL[movementForm.type]?.[pt ? 'labelPt' : 'labelEn']}
              </Typography>
              <FormControl size="small" variant="outlined">
                <InputLabel>{pt ? 'Material' : 'Material'}</InputLabel>
                <Select label={pt ? 'Material' : 'Material'}
                  value={movementForm.stockItemId}
                  onChange={e => setMovementForm({ ...movementForm, stockItemId: e.target.value })}>
                  {items.filter(it => it.isActive).map(it => (
                    <MenuItem key={it.id} value={it.id}>
                      {it.code} · {it.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label={pt ? 'Quantidade' : 'Quantity'}
                size="small" variant="outlined" type="number" autoFocus
                value={movementForm.quantity}
                onChange={e => setMovementForm({ ...movementForm, quantity: e.target.value })}
                helperText={movementForm.type === 'adjust'
                  ? (pt ? 'Pode ser negativa (correcção)' : 'Can be negative')
                  : (pt ? 'Quantidade positiva' : 'Positive')} />
              <TextField label={pt ? 'Notas (opcional)' : 'Notes'}
                size="small" variant="outlined" multiline minRows={2}
                value={movementForm.notes}
                onChange={e => setMovementForm({ ...movementForm, notes: e.target.value })} />
              <Box className={classes.formActions}>
                <Button onClick={() => setMovementForm(null)} disabled={saving}
                  style={{ textTransform: 'none', fontSize: '0.78rem' }}>
                  {pt ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={saveMovement} disabled={saving}
                  style={{
                    background: '#3b82f6', color: '#fff',
                    textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
                    padding: '6px 18px', boxShadow: 'none',
                  }}>
                  {saving ? (pt ? 'A guardar…' : 'Saving…') : (pt ? 'Guardar' : 'Save')}
                </Button>
              </Box>
            </Box>
          )}

          {!selectedVehicleId && (
            <>
              <Box style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                padding: '12px 16px', borderRadius: 10,
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                border: '1px solid #bfdbfe',
              }}>
                <LocalShippingIcon style={{ fontSize: 24, color: '#1d4ed8' }} />
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e3a8a' }}>
                    {pt ? 'Resumo do stock por viatura' : 'Stock overview by vehicle'}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', color: '#1e40af' }}>
                    {pt
                      ? 'Carrega numa viatura para gerir o stock (carregar, devolver, transferir, ajustar).'
                      : 'Click a vehicle to manage its stock.'}
                  </Typography>
                </Box>
                {loadingOverview && <CircularProgress size={16} style={{ color: '#1d4ed8' }} />}
              </Box>

              <Box style={{
                background: '#fff', borderRadius: 10, overflow: 'hidden',
                border: '1px solid #e2e8f0',
              }}>
                <Box style={{
                  display: 'grid',
                  gridTemplateColumns: '1.6fr 1fr 1fr 1.2fr 0.8fr',
                  gap: 10, alignItems: 'center',
                  padding: '10px 16px', background: '#f8fafc',
                  borderBottom: '2px solid #e2e8f0',
                  fontSize: '0.62rem', fontWeight: 800, color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  <span>{pt ? 'Matrícula' : 'Plate'}</span>
                  <span style={{ textAlign: 'right' }}>{pt ? 'Referências' : 'References'}</span>
                  <span style={{ textAlign: 'right' }}>{pt ? 'Unidades' : 'Units'}</span>
                  <span style={{ textAlign: 'right' }}>{pt ? 'Valor estimado' : 'Estimated value'}</span>
                  <span></span>
                </Box>
                {overview.length === 0 && !loadingOverview && (
                  <Box style={{ padding: 24, textAlign: 'center' }}>
                    <Typography style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                      {pt ? 'Sem viaturas disponíveis.' : 'No vehicles available.'}
                    </Typography>
                  </Box>
                )}
                {overview.map(row => (
                  <Box key={row.vehicleId}
                    onClick={() => setSelectedVehicleId(row.vehicleId)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.6fr 1fr 1fr 1.2fr 0.8fr',
                      gap: 10, alignItems: 'center',
                      padding: '12px 16px', cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background 0.15s',
                      background: row.items > 0 ? '#fff' : '#fafafa',
                      opacity: row.isActive === false ? 0.7 : 1,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={e => e.currentTarget.style.background = row.items > 0 ? '#fff' : '#fafafa'}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Box style={{
                        width: 6, height: 36, borderRadius: 3,
                        background: row.items > 0 ? '#22c55e' : '#cbd5e1',
                      }} />
                      <Box>
                        <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>
                          {row.plate}
                        </Typography>
                        <Box style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                          {row.type && (
                            <Typography style={{ fontSize: '0.62rem', color: '#64748b' }}>{row.type}</Typography>
                          )}
                          {row.isActive === false && (
                            <Chip label={pt ? 'Inactiva' : 'Inactive'} size="small"
                              style={{ height: 14, fontSize: '0.52rem', background: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Typography style={{ textAlign: 'right', fontSize: '0.84rem', fontWeight: 700, color: row.items > 0 ? '#0f172a' : '#94a3b8' }}>
                      {row.items}
                    </Typography>
                    <Typography style={{ textAlign: 'right', fontSize: '0.84rem', fontWeight: 700, color: row.units > 0 ? '#0f172a' : '#94a3b8' }}>
                      {row.units}
                    </Typography>
                    <Typography style={{ textAlign: 'right', fontSize: '0.78rem', fontWeight: 600, color: row.value > 0 ? '#16a34a' : '#94a3b8' }}>
                      {row.value > 0 ? `${row.value.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} ${tenantCurrency.symbol}` : '—'}
                    </Typography>
                    <Box style={{ textAlign: 'right' }}>
                      <Chip label={pt ? 'Gerir →' : 'Manage →'} size="small"
                        style={{
                          height: 22, fontSize: '0.64rem', fontWeight: 700,
                          background: '#eff6ff', color: '#1d4ed8',
                        }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {selectedVehicleId && combined.map(it => (
            <Box key={it.id} className={`${classes.card} ${it.vehicleQty === 0 ? classes.cardZero : ''}`}>
              <Box>
                <Typography className={classes.cardName}>{it.name}</Typography>
                <Typography className={classes.cardMeta}>
                  {it.code}{it.category ? ` · ${it.category}` : ''}
                </Typography>
              </Box>
              <Typography style={{ fontSize: '0.7rem', color: '#64748b' }}>
                {pt ? 'Central' : 'Central'}: <b>{it.centralStock}</b> {it.unit}
              </Typography>
              <Box className={classes.qty}>
                {it.vehicleQty} {it.unit}
              </Box>
              <Button className={classes.actionBtn}
                onClick={() => openMovementForm('load_vehicle', it.id)}
                style={{ background: '#dbeafe', color: '#1e40af' }}>
                {pt ? '+ Carregar' : '+ Load'}
              </Button>
              <Button className={classes.actionBtn}
                onClick={() => openMovementForm('unload_vehicle', it.id)}
                disabled={it.vehicleQty <= 0}
                style={{ background: it.vehicleQty > 0 ? '#fef3c7' : '#f1f5f9', color: it.vehicleQty > 0 ? '#92400e' : '#94a3b8' }}>
                {pt ? '↶ Devolver' : '↶ Unload'}
              </Button>
            </Box>
          ))}
        </Box>

        {selectedVehicleId && (
          <Box className={classes.drawer}>
            <Box className={classes.drawerHeader}>
              <HistoryIcon style={{ fontSize: 16, color: '#64748b' }} />
              {pt ? `Histórico · ${selectedVehicle?.plate || ''}` : `History · ${selectedVehicle?.plate || ''}`}
            </Box>
            <Box className={classes.drawerList}>
              {movements.length === 0 && (
                <Typography style={{ fontSize: '0.74rem', color: '#94a3b8', padding: 12 }}>
                  {pt ? 'Sem movimentos ainda.' : 'No movements yet.'}
                </Typography>
              )}
              {movements.map(m => {
                const meta = MOVEMENT_LABEL[m.type];
                return (
                  <Box key={m.id} className={classes.movementRow}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <Chip size="small" label={meta?.[pt ? 'labelPt' : 'labelEn'] || m.type}
                        style={{
                          height: 16, fontSize: '0.58rem', fontWeight: 700,
                          background: `${meta?.color || '#64748b'}1A`,
                          color: meta?.color || '#64748b',
                        }} />
                      <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#0f172a' }}>
                        {m.quantity} {m.stockItemUnit}
                      </Typography>
                    </Box>
                    <Typography style={{ fontSize: '0.66rem', color: '#475569' }}>
                      {m.stockItemCode} · {m.stockItemName}
                    </Typography>
                    {m.orderNumber && (
                      <Typography style={{ fontSize: '0.6rem', color: '#dc2626', fontWeight: 700 }}>
                        OT {m.orderNumber}
                      </Typography>
                    )}
                    {m.notes && (
                      <Typography style={{ fontSize: '0.62rem', color: '#64748b', fontStyle: 'italic' }}>
                        {m.notes}
                      </Typography>
                    )}
                    <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', marginTop: 2 }}>
                      {new Date(m.createdAt).toLocaleString('pt-PT')} · {m.driverName || m.userName || '—'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default StockByVehicleView;
